import Cookie from 'js-cookie';
import map from 'lodash/map';
import { action, computed, observable } from 'mobx';
import React from 'react';
import { campaign_types } from '@/common/models/campaign-shared';
import { DawaAddress } from '@/common/models/dawa';
import InternetCampaign from '@/common/models/internet-campaign';
import { AddressInfo, BBInfoItem, TVPackageItem } from '@/common/models/sows';
import {
    DRUPAL_COOKIE_ADDRESS_FULL,
    DRUPAL_COOKIE_SEGMENT_ID,
    DRUPAL_COOKIE_TECHNOLOGY,
    STOFA_BAROLO_REDIRECT_URL_COOKIE,
} from '@/common/utilities/cookies';
import { getConfig, STANDARD_PRICES } from '@/common/utilities/settings';
import { uniq, uniqBy } from 'lodash';

export function mapCampaignOntoInternetProduct(
    product: BBInfoItem,
    campaign_type: campaign_types
): BBInfoItem {
    const campaign = new InternetCampaign(campaign_type, product);

    return {
        ...product,
        offer_code: campaign.getOfferCode(),
        price: {
            initial: campaign.getInitialFee(),
            recurrent: campaign.getRecurrent(), // Månedlig betaling
        },
        total: {
            initial: campaign.getInitialFee(), // Oprettelsesgebyr total
            recurrent: campaign.getRecurrent({ includeAddons: true }), // Månedlig betaling total
        },

        'exception-price': {
            initial: campaign.getInitialFee(), // "Oprettelsesgebyr Subtotal" i SOWS
            recurrent: campaign.getRecurrent(), // "Månedlig betaling subtotal" i SOWS
        },
    };
}

export function getSortedAndFilteredBBProducts(bb_response: {
    products: { [p: string]: BBInfoItem };
}): BBInfoItem[] {
    return map(bb_response?.products, (item) => item)
        .filter((item: BBInfoItem): boolean => {
            return !(
                item.technology === 'none' || item.technology === 'unknown'
            );
        })
        .map(
            (item: BBInfoItem): BBInfoItem => {
                return {
                    ...item,
                    price: {
                        ...item?.price,
                        recurrent: Math.round(item?.price?.recurrent),
                    },
                };
            }
        )
        .sort((a: BBInfoItem, b: BBInfoItem) => b.down_speed - a.down_speed);
}

export class BroadbandStore {
    @observable loading_dawa_address: boolean = false;
    @observable dawa_load_error: Error | null = null;
    @observable loading_products: boolean = false;
    @observable products_loaded: boolean = false;
    @observable products_load_error: Error | null = null;
    @observable default_bb_products: BBInfoItem[] = [];
    @observable available_bb_products: BBInfoItem[] = [];
    @observable dawa_address: DawaAddress | null = null;
    @observable dawa_address_text: string = '';
    @observable address_info: AddressInfo | null = null;

    @observable tv_package_submitted: boolean = false;
    @observable selected_tv_package: TVPackageItem | null = null;

    @observable active_bb_campaign: campaign_types = getConfig()
        .activeInternetCampaign;
    @observable selected_bb_product: BBInfoItem | null = null;

    @observable placingOrder: boolean = false;
    @observable submit_error?: Error | undefined;

    @action
    reset(): void {
        Cookie.remove(DRUPAL_COOKIE_ADDRESS_FULL);
        Cookie.remove(DRUPAL_COOKIE_TECHNOLOGY);
        Cookie.remove(DRUPAL_COOKIE_SEGMENT_ID);
        Cookie.remove(STOFA_BAROLO_REDIRECT_URL_COOKIE);
        this.dawa_address = null;
        this.products_loaded = false;
        this.products_load_error = this.dawa_load_error = null;
        window.location.hash = '';
    }

    @computed
    get loading(): boolean {
        return this.loading_dawa_address || this.loading_products;
    }
}

export const broadbandStore = new BroadbandStore();
export const broadbandStoreContext = React.createContext(broadbandStore);
export const useBroadBandStore = () => React.useContext(broadbandStoreContext);
export default useBroadBandStore;
